import React, { useEffect, useRef, useState } from 'react';
import './LandingPage.css';
import { useNavigate } from 'react-router-dom';
import { FaApple, FaGooglePlay, FaLinkedin } from 'react-icons/fa';

const heroBackdrop = '/images/img1/thomas-serer-alUSb8lCE5s-unsplash.jpg';
const heroIllustration = '/images/img1/rival.jpg';
const innovationBackdrop = '/images/img1/fans.jpg';
const featuredRivalryImage = '/images/img1/clark-tibbs-oqStl2L5oxI-unsplash.jpg';
const plusOneCardImage = '/images/img1/rival.jpg';
const contentTamperImage = '/images/img1/contentTamper.jpg';
const envGuardImage = '/images/img1/enguard.jpg';
const smartAmenityImage = '/images/img1/pool.png';

const getInitialLiveRequests = () => 12000 + Math.floor(Math.random() * 1200);
const formatLiveRequests = (count) => {
  if (count >= 1000) {
    const abbreviated = (count / 1000).toFixed(1);
    return abbreviated.endsWith('.0') ? `${Math.round(count / 1000)}k` : `${abbreviated}k`;
  }
  return count.toLocaleString();
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [requestingChallengeCount, setRequestingChallengeCount] = useState(getInitialLiveRequests);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    topic: '',
    message: '',
  });
  const [contactStatus, setContactStatus] = useState('');
  const contactRef = useRef(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setRequestingChallengeCount((prev) => prev + Math.floor(Math.random() * 80) + 15);
    }, 2200);

    return () => clearInterval(interval);
  }, []);

  const formattedRequests = formatLiveRequests(requestingChallengeCount);

  const scrollToContact = () => {
    if (contactRef.current) {
      contactRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleContactChange = (event) => {
    const { name, value } = event.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = (event) => {
    event.preventDefault();
    const { name, email, topic, message } = contactForm;
    if (!name.trim() || !email.trim() || !message.trim()) {
      setContactStatus('Please add your name, email, and a quick message.');
      return;
    }
    const subject = encodeURIComponent(`MEANC contact: ${topic || 'General inquiry'}`);
    const bodyLines = [
      `Name: ${name}`,
      `Email: ${email}`,
      topic ? `Topic: ${topic}` : null,
      '',
      message,
    ].filter(Boolean);
    const body = encodeURIComponent(bodyLines.join('\n'));
    window.location.href = `mailto:services@meanc.net?subject=${subject}&body=${body}`;
    setContactStatus('Opening your email client to send the message.');
  };

  const productCards = [
    {
      key: 'rivalcam',
      title: 'RivalCam',
      badge: 'Live',
      summary: 'Fan-versus-fan challenges with live scoring, trophy cams, and clips you can prove came from the crowd.',
      image: plusOneCardImage,
      accent: 'linear-gradient(135deg, rgba(255,95,109,0.18), rgba(93,220,255,0.14))',
      ctas: [
        { label: 'See rivalries', onClick: () => navigate('/app/rivalry'), variant: 'solid' },
        { label: 'Fan beta reels', onClick: () => navigate('/app/chat'), variant: 'ghost' },
      ],
    },
    {
      key: 'smart-amenity',
      title: 'Smart Amenity',
      badge: 'New',
      summary: 'Property access for pools, gyms, and lounges with tap-to-enter, live occupancy, and simple guest passes.',
      image: smartAmenityImage,
      accent: 'linear-gradient(135deg, rgba(111,255,199,0.22), rgba(41,121,255,0.14))',
      ctas: [
        { label: 'Book a demo', href: 'https://smartamenity.net', variant: 'solid' },
      ],
    },
    {
      key: 'content-tamper',
      title: 'ContentTamper',
      badge: 'Private Beta',
      summary: 'Editors can lock files, see who touched what, and export proof right next to the footage.',
      image: contentTamperImage,
      accent: 'linear-gradient(135deg, rgba(93,220,255,0.18), rgba(242,54,138,0.12))',
      ctas: [
        { label: 'Join waitlist', href: 'mailto:services@meanc.net?subject=ContentTamper%20Waitlist', variant: 'solid' },
      ],
    },
    {
      key: 'env-guard',
      title: 'EnvGuard',
      badge: 'Security',
      summary: 'Rotate secrets, watch API usage, and keep environments tidy without slowing engineers down.',
      image: envGuardImage,
      accent: 'linear-gradient(135deg, rgba(14,21,61,0.22), rgba(0,140,114,0.2))',
      ctas: [
        { label: 'Talk to security', href: 'mailto:services@meanc.net?subject=EnvGuard%20Consult', variant: 'solid' },
      ],
    },
  ];

  return (
    <>
      <section
        className="hero-section"
        style={{
          backgroundImage: `radial-gradient(circle at 20% 20%, rgba(93,220,255,0.16), transparent 35%), radial-gradient(circle at 80% 0%, rgba(242,54,138,0.12), transparent 32%), linear-gradient(120deg, rgba(4,7,26,0.9), rgba(7,12,38,0.88)), url(${heroBackdrop})`,
        }}
      >
        <div className="hero-container">
          <div className="hero-content">
            <p className="hero-eyebrow">MEANC LLC • Software Company</p>
            <h1>Software that keeps fans loud, buildings smooth, and creators safe.</h1>
            <p className="hero-subtitle">
              Four focused products: RivalCam for live fan battles, Smart Amenity for effortless property access, ContentTamper for provable creative work, and EnvGuard for clean, safe developer environments. Designed to look polished and stay steady when traffic surges.
            </p>

            <div className="hero-tags">
              <span>Realtime video</span>
              <span>Zero-trust content</span>
              <span>Smart amenities</span>
              <span>Developer console</span>
            </div>

          <div className="hero-cta">
            <button className="cta-btn" onClick={() => navigate('/access-lab')}>
              Launch Console
            </button>
            <button className="cta-btn-tertiary" onClick={scrollToContact}>
              Talk to MEANC
            </button>
          </div>
            <div className="hero-store-row">
              <button className="store-pill is-disabled" disabled>
                <FaApple />
                <span>App Store (coming soon)</span>
              </button>
              <button className="store-pill is-disabled" disabled>
                <FaGooglePlay />
                <span>Google Play (coming soon)</span>
              </button>
            </div>
          </div>

          <div className="hero-visual">
            <img src={heroIllustration} alt="Product illustration showing dashboards" />
            <div className="hero-card">
              <p className="hero-card-label">Featured launch</p>
              <h3>RivalCam + Smart Amenity</h3>
              <p className="hero-card-text">
                Capture rival reactions, then unlock premium amenities with the same identity and trust layer.
              </p>
              <button className="project-button" onClick={() => navigate('/app/rivalry')}>
                Explore live rivalries
              </button>
              <div className="hero-live-card">
                <div className="hero-live-label">
                  <span className="live-dot" />
                  Active users right now
                </div>
                <p className="hero-live-value">
                  {formattedRequests} live fan + resident pings
                </p>
                <p className="hero-live-subline">every few seconds a new request arrives</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="featured-app">
        <div className="featured-media">
          <img src={featuredRivalryImage} alt="ChallengeRivalry stadium atmosphere" />
          <div className="featured-badge">MEANC Suite</div>
        </div>
        <div className="featured-content">
          <p className="hero-eyebrow">What we build</p>
          <h2>Four products, four clear jobs</h2>
          <p>
            Each product is narrow on purpose so it’s easy to understand: RivalCam is for fan contests, Smart Amenity is for resident access, ContentTamper is for creator evidence, and EnvGuard is for engineering hygiene.
          </p>
          <ul className="featured-list">
            <li>RivalCam: realtime rivalry pipelines tuned for milliseconds</li>
            <li>Smart Amenity: access control with live occupancy and passes</li>
            <li>ContentTamper: proof and audit trails for creative teams</li>
            <li>EnvGuard: clean environments and API guardrails for devs</li>
          </ul>
          <div className="hero-cta">
            <button className="cta-btn" onClick={() => navigate('/access-lab')}>
              Launch Access Lab
            </button>
            <button className="cta-btn-secondary" onClick={scrollToContact}>
              Talk to the team
            </button>
          </div>
          <div className="store-badges featured-badges">
            <button className="store-pill is-disabled" disabled>
              <FaApple />
              <span>App Store (coming soon)</span>
            </button>
            <button className="store-pill is-disabled" disabled>
              <FaGooglePlay />
              <span>Google Play (coming soon)</span>
            </button>
          </div>
        </div>
      </section>
      <section
        className="innovation-banner"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(7,11,40,0.85), rgba(12,22,64,0.7)), url(${innovationBackdrop})`,
        }}
      >
        <div className="banner-text">
          <p className="hero-eyebrow">Coming soon</p>
          <h2>Security storytelling with ContentTamper</h2>
          <p>
            We are polishing an illustrated security layer that keeps creators in control of their footage from the
            first upload through verified handoff.
          </p>
          <div className="hero-cta">
            <button className="cta-btn" onClick={() => navigate('/contact')}>
              Join the waitlist
            </button>
            <button className="cta-btn-secondary" onClick={() => navigate('/envguard')}>
              See platform highlights
            </button>
          </div>
        </div>
      </section>
      

      <section className="projects-section" id="products">
        <div className="section-header">
          <div>
            <p className="hero-eyebrow">MEANC product line</p>
            <h2 className="section-title">Explore the MEANC suite</h2>
          </div>
          <p className="section-subtitle">
            Every product shares one backbone: realtime pipelines, motion-led UX, and trustworthy security so launches feel premium and stay resilient.
          </p>
        </div>

        <div className="project-grid project-grid--modern">
          {productCards.map((card) => (
            <article
              key={card.key}
              className={`project-card modern-card${card.badge === 'Live' ? ' is-highlight' : ''}`}
              style={{ backgroundImage: card.accent }}
            >
              <figure className="project-figure">
                <img src={card.image} className="project-image" alt={card.title} />
              </figure>
              <div className="card-meta">
                <span className="badge badge-soft">{card.badge}</span>
              </div>
              <h3>{card.title}</h3>
              <p>{card.summary}</p>
              <div className="product-actions">
                {card.ctas?.map((cta) => {
                  const className = cta.variant === 'ghost' ? 'project-button ghost' : 'project-button';
                  if (cta.href) {
                    return (
                      <a
                        key={cta.label}
                        className={className}
                        href={cta.href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {cta.label}
                      </a>
                    );
                  }
                  return (
                    <button key={cta.label} className={className} type="button" onClick={cta.onClick}>
                      {cta.label}
                    </button>
                  );
                })}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="smart-amenity" id="smart-amenity">
        <div className="smart-amenity__copy">
          <p className="hero-eyebrow">Smart Amenity</p>
          <h2>Frictionless, data-aware amenities for modern properties</h2>
          <p>
            Residents tap in once and move through the gym, pool, or lounge without front-desk friction. Property teams see live occupancy, manage guest passes, and keep waivers in one place.
          </p>
          <div className="hero-cta">
            <button className="cta-btn" onClick={() => window.open('https://smartamenity.net', '_blank', 'noopener,noreferrer')}>
              Open smartamenity.net
            </button>
          </div>
          <div className="smart-amenity__chips">
            <span>Live occupancy</span>
            <span>Guest passes</span>
            <span>Access control</span>
            <span>Resident identity</span>
          </div>
        </div>
        <div className="smart-amenity__visual">
          <div className="glass-card">
            <img src={smartAmenityImage} alt="Residents at a pool amenity" />
            <div className="glass-card__meta">
              <span className="badge badge-soft">New</span>
              <p>Trusted by properties that want premium experiences without friction.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-section" id="contact" ref={contactRef}>
        <div className="contact-copy">
          <p className="hero-eyebrow">Talk to MEANC</p>
          <h2>Tell us about your launch, property, or security needs</h2>
          <p>
            Share a quick note and we’ll follow up with the right team—fan engagement, smart amenities, or security engineering.
          </p>
          <ul className="contact-list">
            <li>Response within 1 business day</li>
            <li>White-glove onboarding for partners</li>
            <li>Privacy-first: we only use your details to reply</li>
          </ul>
        </div>
        <div className="contact-card">
          <form className="contact-form" onSubmit={handleContactSubmit}>
            <label>
              Full name
              <input
                type="text"
                name="name"
                placeholder="Your name"
                value={contactForm.name}
                onChange={handleContactChange}
                required
              />
            </label>
            <label>
              Work email
              <input
                type="email"
                name="email"
                placeholder="you@company.com"
                value={contactForm.email}
                onChange={handleContactChange}
                required
              />
            </label>
            <label>
              Topic
              <select name="topic" value={contactForm.topic} onChange={handleContactChange}>
                <option value="">Select a topic</option>
                <option value="RivalCam">RivalCam / live fan apps</option>
                <option value="Smart Amenity">Smart Amenity / property tech</option>
                <option value="ContentTamper">ContentTamper / creator security</option>
                <option value="EnvGuard">EnvGuard / secrets & APIs</option>
                <option value="Partnerships">Partnerships / other</option>
              </select>
            </label>
            <label>
              Message
              <textarea
                name="message"
                rows="4"
                placeholder="What would you like to build or learn about?"
                value={contactForm.message}
                onChange={handleContactChange}
                required
              />
            </label>
            <p className="contact-hint">Submit opens your email client to send to services@meanc.net.</p>
            {contactStatus && <p className="contact-status">{contactStatus}</p>}
            <button type="submit" className="cta-btn contact-submit">Send message</button>
          </form>
        </div>
      </section>

      <section className="about-company" id="about">
        <div className="about-content">
          <p className="hero-eyebrow">About MEANC LLC</p>
          <h2 className="section-title">We build expressive software that holds up in production</h2>
          <p className="about-text">
            MEANC LLC builds a small set of products that do different jobs: hype fans, smooth building access, protect creative work, and keep engineering tidy. We pair vivid interfaces with grounded engineering so partners see value fast and stay confident when they scale.
          </p>
          <div className="founder-card">
            <div className="founder-head">
              <img className="founder-avatar" src="/images/img1/mosesPhoto.png" alt="Moussa Diallo" />
              <div>
                <p className="founder-label">Founder</p>
                <h3>Moussa Diallo</h3>
              </div>
            </div>
            <p className="founder-bio">
              I started MEANC to ship software that performs under pressure—whether that’s a packed stadium or a busy property lobby.
              I stay close to the work: designing flows, reviewing code, and onboarding partners so every product feels deliberate and reliable.
            </p>
            <div className="founder-actions">
              <a className="project-button linkedin-btn" href="https://www.linkedin.com/in/mosesdm/" target="_blank" rel="noreferrer">
                <FaLinkedin /> Connect on LinkedIn
              </a>
            </div>
          </div>
          <div className="values-grid">
            <div className="value-box">
              <h4>⚡ Performance</h4>
              <p>Realtime pipelines tuned for milliseconds.</p>
            </div>
            <div className="value-box">
              <h4>🔐 Security</h4>
              <p>Zero-trust content protection across every upload.</p>
            </div>
            <div className="value-box">
              <h4>🎨 Storytelling</h4>
              <p>Motion-forward UX that explains value in seconds.</p>
            </div>
            <div className="value-box">
              <h4>🤝 Service</h4>
              <p>White-glove launches, support, and migration.</p>
            </div>
          </div>
        </div>
        <div className="about-illustration">
          <img
            src="https://images.unsplash.com/photo-1487014679447-9f8336841d58?auto=format&fit=crop&w=1200&q=80"
            alt="Design team collaborating"
          />
          <div className="about-metrics">
            <div>
              <strong>2022</strong>
              <span>Founded</span>
            </div>
            <div>
              <strong>3</strong>
              <span>Product lines</span>
            </div>
            <div>
              <strong>∞</strong>
              <span>Live fan moments</span>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
         {/* <div className="footer-links">
          <a href="/terms">Terms</a>
          <a href="/privacy">Privacy</a>
          <a href="/contact">Contact</a>
        </div> 
        <p>© 2025 MEANC Technologies – All Rights Reserved</p> */}
      </footer>
    </>
  );
};

export default LandingPage;
